import React, { useEffect, useState } from 'react'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from './messages'
import { loginRoute, useRouter, ScanNowRoute } from '@src/routes'
import { ProductDetailFragment } from '@src/fragments/ProductDetail.generated'
import { MaterialDetailFragment } from '@src/queries/MaterialDetailQuery.generated'
import { useEmproveUserDataV2LazyQuery } from '@src/queries/EmproveUserDataQueryV2.generated'
import { UserType } from '@src/types/graphql-types'
import { Divider, Box } from '@material-ui/core'
import { TierLinks } from '../ProductDetail/ProductDocumentation/TierLinks'
import { useUserSession } from '@src/utils/useUserSession'
import { setupTiers } from '../ProductDetail/ProductDocumentation/tierUtils'
import { useCurrentUser } from '@utils/useCurrentUser'
import DocumentDetails from '../ProductDetail/Emprove/DocumentDetails'

interface ScanNowLinksProps {
  material: MaterialDetailFragment
  productDetailData: ProductDetailFragment
  emproveDocuments: any
  isBlueProduct: boolean
  barcode: string
}

const useStyles = makeStyles((theme: Theme) => ({
  link: {
    fontSize: theme.typography.pxToRem(14),
    paddingBottom: theme.spacing(4),
    '&:last-of-type': {
      paddingBottom: 0,
    },
  },
  resourcesContainer: {
    width: '100%',
    boxShadow: '0 0 4px 0 rgba(0, 0, 0, 0.25)',
    padding: '26px 24px 12px',
    height: 'fit-content',
    marginTop: '32px',
    [theme.breakpoints.up('md')]: {
      marginTop: '84px',
      width: '290px',
    },
    '& > hr:first-of-type': {
      display: 'none',
    },
  },
  resourcesHeading: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 900,
  },
  resourcesLink: {
    display: 'inline-block',
    paddingBottom: '12px',
  },
  expandTrigger: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.primary.main,
    marginTop: theme.spacing(4),
    [theme.breakpoints.up('md')]: {
      fontSize: theme.typography.pxToRem(16),
    },
    '&:last-of-type': {
      paddingBottom: '0',
    },
  },
  caretIcon: {
    fontSize: theme.typography.pxToRem(10),
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(1 / 2),
    transform: 'rotate(0deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  caretIconExpanded: {
    transform: 'rotate(180deg)',
  },
  sectionHeading: {
    paddingBottom: '10px',
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(16),
  },
  tierSection: {
    paddingTop: '20px',
    paddingBottom: '20px',
    '&:not(:last-of-type)': {
      paddingBottom: '4px',
    },
    '& h3': {
      paddingBottom: '10px',
      fontWeight: 700,
      fontSize: theme.typography.pxToRem(16),
    },
  },
  emproveHeading: {
    paddingTop: '12px',
    paddingBottom: '8px',
  },
  emproveDocuments: {
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: '18px',
  },
}))

const ScanNowLinks: React.FC<ScanNowLinksProps> = ({
  barcode,
  material,
  productDetailData,
  emproveDocuments,
  isBlueProduct,
}) => {
  const classes = useStyles()
  const router = useRouter()
  const intl = useIntl()
  const {
    userSession: { country, language },
  } = useUserSession()
  const { tier1Links, tier2Links } = setupTiers(
    productDetailData.links,
    country,
    language,
    intl
  )
  const [shouldRedirect, setShouldRedirect] = useState(false)
  const [emailSent, setEmailSent] = useState(false)

  const [getEmproveUserDataV2, { data }] = useEmproveUserDataV2LazyQuery({
    fetchPolicy: 'cache-and-network',
  })

  const { userIsLoggedIn, currentUser } = useCurrentUser()

  useEffect(() => {
    if (currentUser?.__typename === 'LoggedInUser') {
      getEmproveUserDataV2()
    }
  }, [getEmproveUserDataV2, currentUser, userIsLoggedIn])

  if (shouldRedirect) {
    router.push({
      pathname: loginRoute.index(),
      query: {
        ...{},
        redirect: `${ScanNowRoute.index()}?barcode=${barcode}`,
        sessionExpired: currentUser?.type === UserType.RememberMe,
      },
    })
  }
  const hasDocumentLinks =
    emproveDocuments?.length ||
    Object.keys(tier1Links)?.length ||
    Object.keys(tier2Links)?.length

  if (!hasDocumentLinks) return null

  return (
    <div className={classes.resourcesContainer}>
      <h1 className={classes.resourcesHeading}>
        <FormattedMessage {...messages.RESOURCES_AND_SUPPORTING_DOCS} />
      </h1>
      {emproveDocuments.length ? (
        <>
          <Divider />
          <Box mt={3} mb={2}>
            <h3>{intl.formatMessage(messages.EMPROVE_DOSSIERS)}</h3>
            {!userIsLoggedIn ? (
              <h3>{`(${intl.formatMessage(messages.REQUIRES_LOGIN)})`}</h3>
            ) : null}
          </Box>
          <div className={classes.emproveDocuments}>
            {emproveDocuments.map((document, index) => (
              <DocumentDetails
                document={document}
                userData={data}
                setShouldRedirect={setShouldRedirect}
                productName={productDetailData?.name}
                setEmailSent={setEmailSent}
                emailSent={emailSent}
                isScanNow
                key={`document-details-${index}`}
              />
            ))}
          </div>
        </>
      ) : null}
      {Object.keys(tier1Links).length ? (
        <>
          <Divider />
          <TierLinks
            tierType={'tier1'}
            tierLinks={tier1Links}
            classes={classes}
            country={country}
            language={language}
            productNumber={material.product}
            isBlueProduct={isBlueProduct}
            showDivider
          />
        </>
      ) : null}
      {Object.keys(tier2Links).length ? (
        <>
          <Divider />
          <TierLinks
            tierType={'tier2'}
            tierLinks={tier2Links}
            classes={classes}
            country={country}
            language={language}
            productNumber={material.product}
            isBlueProduct={isBlueProduct}
            showDivider
          />
        </>
      ) : null}
    </div>
  )
}

export default ScanNowLinks
